if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          dots: this.querySelector('[id^="GalleryDots"]'),
        };
        this.mql = window.matchMedia('(min-width: 990px)');
        if (!this.elements.thumbnails && !this.elements.dots) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
        }
        if (this.elements.dots) {
          this.elements.dots.querySelectorAll('[data-target]').forEach((dot) => {
            dot.addEventListener('click', this.setActiveMedia.bind(this, dot.dataset.target, false));
          });
        }
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        if (this.dataset.desktopLayout === 'thumbnail_left') {
          this.initThumbnailRail();
          // The featured media is always rendered first; other scripts (eg.
          // syncing the gallery to the initially selected variant) can leave
          // the horizontal scroll position stale on first paint, showing a
          // sliver of margin until the next scroll corrects it. Force it back.
          if (this.elements.viewer.slider) {
            requestAnimationFrame(() => this.elements.viewer.slider.scrollTo({ left: 0 }));
          }
        }
      }

      initThumbnailRail() {
        const rail = this.elements.thumbnails;
        const list = rail && rail.querySelector('.thumbnail-list');
        const upButton = this.querySelector('[data-thumbnail-rail-control="prev"]');
        const downButton = this.querySelector('[data-thumbnail-rail-control="next"]');
        if (!list || !upButton || !downButton) return;

        this.thumbnailRail = { rail, list, upButton, downButton };

        // The rail clips the thumbnails that are out of view, so they never enter
        // the viewport and lazy loading leaves them blank forever - that blank
        // space is what shows up as an empty column. They are a handful: load them all.
        list.querySelectorAll('img').forEach((image) => {
          if (image.loading === 'lazy') image.loading = 'eager';
        });

        // The stylesheet sets `scroll-behavior: smooth` on this container, which
        // silently swallows the scrolls that bring the active thumbnail into view.
        list.style.scrollBehavior = 'auto';
        rail.style.overflow = 'hidden';

        this.fitThumbnailRail = this.fitThumbnailRail.bind(this);
        window.addEventListener('resize', () => {
          this.fitThumbnailRail();
          this.watchThumbnailRail();
        });

        // The column is sized against the height of the main image, which is not
        // final on the first paint. Everything below re-runs the fit once the
        // layout settles; without it the rail keeps whatever size it computed too
        // early and only a window resize (opening devtools, for one) fixes it.
        if ('ResizeObserver' in window) {
          this.thumbnailRailObserver = new ResizeObserver(this.fitThumbnailRail);
          [this.elements.viewer, rail].forEach((element) => this.thumbnailRailObserver.observe(element));
        }
        if (document.readyState !== 'complete') {
          window.addEventListener('load', this.fitThumbnailRail, { once: true });
        }
        this.elements.viewer.querySelectorAll('img').forEach((image) => {
          if (!image.complete) image.addEventListener('load', this.fitThumbnailRail, { once: true });
        });
        list.querySelectorAll('img').forEach((image) => {
          if (!image.complete) image.addEventListener('load', this.fitThumbnailRail, { once: true });
        });

        this.fitThumbnailRail();
        requestAnimationFrame(this.fitThumbnailRail);
        this.watchThumbnailRail();

        const goToAdjacentMedia = (offset) => {
          // Every rendered thumbnail, including the ones on the other pages -
          // that is what lets the arrows walk past the end of the current page.
          const items = this.availableThumbnails();
          const activeIndex = items.findIndex(
            (item) => item.querySelector('button')?.getAttribute('aria-current') === 'true'
          );
          if (activeIndex === -1) return;
          const target = items[activeIndex + offset];
          if (target) this.setActiveMedia(target.dataset.target, false);
        };
        upButton.addEventListener('click', () => goToAdjacentMedia(-1));
        downButton.addEventListener('click', () => goToAdjacentMedia(1));
      }

      // Polls the height of the column for a few seconds after load and re-fits
      // whenever it changes. The observers above should do this, but if any of
      // them fails to fire the rail keeps a size computed before the main image
      // had settled - visibly wrong until the window happens to be resized.
      // Stops once the height has held steady and the page has finished loading.
      watchThumbnailRail() {
        clearInterval(this.thumbnailRailWatch);
        if (!this.thumbnailRail) return;

        // Watches the main image, the height the column is fitted to; the list's
        // own height is set from here, so it would never report a change.
        let previous = this.elements.viewer.clientHeight;
        let steady = 0;

        this.thumbnailRailWatch = setInterval(() => {
          const height = this.elements.viewer.clientHeight;
          if (height !== previous) {
            previous = height;
            steady = 0;
            this.fitThumbnailRail();
            return;
          }
          steady += 1;
          if (steady >= 10 && document.readyState === 'complete') clearInterval(this.thumbnailRailWatch);
        }, 200);
      }

      // Sizes the thumbnails so that a whole number of them (six by default, set
      // with --thumbnail-rail-visible) fills the height of the main image exactly.
      // Nothing is left half-cut and no gap is left at the bottom of the column.
      fitThumbnailRail() {
        if (!this.thumbnailRail) return;
        const { rail, list, upButton, downButton } = this.thumbnailRail;

        if (!this.mql.matches) {
          rail.style.removeProperty('--thumbnail-rail-item-height');
          return;
        }

        const items = this.availableThumbnails();
        if (!items.length || !this.elements.viewer.clientHeight) return;

        const railStyles = getComputedStyle(rail);
        const gap = parseFloat(getComputedStyle(list).rowGap) || 0;
        const cap = parseInt(railStyles.getPropertyValue('--thumbnail-rail-visible'), 10) || 6;
        const visible = Math.min(cap, items.length);

        // Anchored to the main image, which is exactly what the column has to
        // match, instead of to the list's own height: that one depends on how the
        // flex layout has settled and can still be short when this runs, which
        // left the column ending well above the image with a thumbnail missing.
        const railGap = parseFloat(railStyles.rowGap) || 0;
        const arrows = upButton.offsetHeight + downButton.offsetHeight + railGap * 2;
        const available = Math.round(this.elements.viewer.clientHeight - arrows);
        if (available <= 0) return;

        // Set explicitly, so the column is the height of the image whatever the
        // flex layout would have given it.
        list.style.flex = '0 0 auto';
        list.style.height = `${available}px`;

        // Kept to two decimals rather than rounded down, so the thumbnails fill
        // the column to the pixel instead of leaving a sliver at the bottom.
        let size = Math.floor(((available - gap * (visible - 1)) / visible) * 100) / 100;

        // With very few images, filling the height would stretch each thumbnail
        // into a tall strip. Cap it and accept a little space at the bottom.
        const ratio = parseFloat(railStyles.getPropertyValue('--thumbnail-rail-max-ratio')) || 1.6;
        const sample = items.find((item) => item.getBoundingClientRect().width > 0);
        if (sample) size = Math.min(size, sample.getBoundingClientRect().width * ratio);

        // The observers below watch the rail itself, so bail out when nothing
        // changed rather than writing the same value and waking them again.
        if (Math.abs((this.thumbnailRailSize || 0) - size) < 0.5) return;
        this.thumbnailRailSize = size;
        rail.style.setProperty('--thumbnail-rail-item-height', `${size}px`);

        const active = list.querySelector('[aria-current]');
        this.showThumbnailPage(active ? active.closest('.thumbnail-list__item') : items[0]);
      }

      // Every thumbnail that the theme actually renders: those of variant images
      // are hidden with `display: none` and their media is not rendered at all,
      // while the ones this rail hides to page are marked and still count.
      availableThumbnails() {
        return Array.from(this.thumbnailRail.list.querySelectorAll('.thumbnail-list__item')).filter(
          (item) => item.hasAttribute('data-rail-paged') || item.getBoundingClientRect().height > 0
        );
      }

      // Shows the page of thumbnails that contains this one, by rendering only
      // those thumbnails and hiding the rest. No scrolling, no transform: nothing
      // in the stylesheet can prevent the column from changing page.
      // The last page is aligned to the end (with seven images it shows 2-7
      // rather than just the seventh), so a page is never half empty.
      showThumbnailPage(thumbnail) {
        if (!this.thumbnailRail) return;
        const items = this.availableThumbnails();
        if (!items.length) return;

        const cap =
          parseInt(getComputedStyle(this.thumbnailRail.rail).getPropertyValue('--thumbnail-rail-visible'), 10) || 6;
        const visible = Math.min(cap, items.length);
        const index = Math.max(0, items.indexOf(thumbnail));

        let start = this.thumbnailPageStart || 0;
        if (index < start) start = index;
        if (index > start + visible - 1) start = index - visible + 1;

        // Keep one thumbnail of lead beyond the active one whenever there is one,
        // so reaching the last thumbnail of a page already reveals the next image
        // instead of waiting for it to be selected.
        if (index === start + visible - 1 && index < items.length - 1) start += 1;
        if (index === start && index > 0) start -= 1;

        start = Math.max(0, Math.min(start, items.length - visible));
        this.thumbnailPageStart = start;

        items.forEach((item, position) => {
          if (position < start || position >= start + visible) {
            item.style.display = 'none';
            item.setAttribute('data-rail-paged', '');
          } else {
            item.style.removeProperty('display');
            item.removeAttribute('data-rail-paged');
          }
        });
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this.fitThumbnailRail);
        this.thumbnailRailObserver?.disconnect();
        clearInterval(this.thumbnailRailWatch);
      }

      onSlideChanged(event) {
        const mediaId = event.detail.currentElement.dataset.mediaId;
        if (this.elements.thumbnails) {
          this.setActiveThumbnail(this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`));
        }
        if (this.elements.dots) {
          this.setActiveDot(this.elements.dots.querySelector(`[data-target="${mediaId}"]`));
        }
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          this.setActiveThumbnail(activeThumbnail);
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
        if (this.elements.dots) {
          this.setActiveDot(this.elements.dots.querySelector(`[data-target="${mediaId}"]`));
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        if (this.dataset.desktopLayout === 'thumbnail_left' && this.mql.matches) {
          this.showThumbnailPage(thumbnail);
          return;
        }

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      setActiveDot(dot) {
        if (!this.elements.dots || !dot) return;

        this.elements.dots.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
        dot.setAttribute('aria-current', true);
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
